import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import SignUpIcon from '@mui/icons-material/HowToReg'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { Box, Divider, Typography, useTheme } from '@mui/material'

import { HelperWrapper, SettingsSwitch } from 'src/components'
import { SettingsItem } from './settings-item.component'
import { SettingsHashTags } from '../hash-tags'
import {
  useAllowScanToErnSetting,
  useGetAvailableModels,
  useSendTgCustomMessage,
  useUpdateDescriptionEntityPayload,
  useUpdateModelsPayload,
  useUpdateSignUpSettings,
  useUpdateTgChatList,
  useUpdateToolsPayload,
  useUpdateTraicyDefinition,
  useUpdateTraicyPostsPayload,
} from 'src/hooks'
import {
  ISettingsRespDto,
  ESettingsPromptFields,
  ESettingsModelOrToolFields,
  ISettingsDescriptionEntityPayload,
  ESettingsTgChatIdsFields,
  IUpdateToolsPayloadDto,
  ETraicyPostsPayloadFields,
  IUpdateTraicyPostsPayloadDto,
} from 'src/types'

import { useStyles } from './../styles'
import { ModelSelect } from '../model-select'
import { EPromptsVariables } from 'src/constants'
import { GlobalSettingsVariables } from './global-variables/global-variables.component'
import { ToolsSection } from '../tools-section'
import { DescriptionEntityPrompts } from '../description-entity-prompts'
import { SettingsTgChatsList } from '../tg-chats-list'
import { SettingsTgSendCustomMessage } from '../tg-send-custom-message'
import { TgSendMessagePermissions } from '../tg-send-message-permissions'
import { TraicyPostImageSwitch } from '../traicy-post-image-switch'

interface IProps {
  settings: ISettingsRespDto | null
  isSettingsFetching: boolean
  isSettingsLoading: boolean
}

export const DesktopSettings = ({ isSettingsFetching, isSettingsLoading, settings }: IProps) => {
  const [t] = useTranslation()

  const theme = useTheme()
  const classes = useStyles()

  const { mutate: updateSignUp, isLoading: signUpIsLoading } = useUpdateSignUpSettings()
  const { mutate: scanToEarnSetting, isLoading: isScanToEarnSettingLoading } =
    useAllowScanToErnSetting()
  const { mutate: updateTraicyPrompt, isLoading: isTraicyPromptLoading } =
    useUpdateTraicyDefinition()

  const { mutate: updateModelsPayload, isLoading: isModelsPayloadLoading } =
    useUpdateModelsPayload()
  const { mutate: updateToolsPayload } = useUpdateToolsPayload()
  const { mutate: updateDescriptionEntityPayload, isLoading: isDescriptionEntityPayloadLoading } =
    useUpdateDescriptionEntityPayload()
  const { mutate: updateTgChatList } = useUpdateTgChatList()
  const { mutate: sendTgCustomMessage, isLoading: isSendTgCustomMessageLoading } =
    useSendTgCustomMessage()
  const { mutate: updateTraicyPostsPayload, isLoading: isUpdateTraicyPostsPayloadLoading } =
    useUpdateTraicyPostsPayload()
  const { data: modelsList, isLoading: idModelsListLoading } = useGetAvailableModels()

  const handleUpdateSignUp = (e: ChangeEvent<HTMLInputElement>) => {
    updateSignUp({ enabled: e.target.checked })
  }

  const handleUpdateScanToEarn = (e: ChangeEvent<HTMLInputElement>) => {
    scanToEarnSetting({ enabled: e.target.checked })
  }

  const handleUpdateTraicyPrompt = (text: string, field: ESettingsPromptFields) => {
    updateTraicyPrompt({ text, field })
  }

  const handleUpdateModelsPayload = (model: string, field: ESettingsModelOrToolFields) => {
    updateModelsPayload({ model, field })
  }

  const handleUpdateToolsPayload = (dto: IUpdateToolsPayloadDto) => {
    updateToolsPayload(dto)
  }

  const handleUpdateTgChatList = (list: string[], field: ESettingsTgChatIdsFields) => {
    updateTgChatList({ list, field })
  }

  const handleSendTgCustomMessage = (message: string) => {
    sendTgCustomMessage({ message })
  }

  const handleUodateTraicyPostPayload = (dto: IUpdateTraicyPostsPayloadDto) => {
    updateTraicyPostsPayload(dto)
  }

  const handleUpdateDescriptionEntityPayload = (
    desc: Partial<ISettingsDescriptionEntityPayload>,
  ) => {
    updateDescriptionEntityPayload(desc)
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1044px', p: '10px' }}
    >
      <h3 className={classes.itemTitle}>
        <HelperWrapper t="app-settings-header-title" />
      </h3>

      <div className={classes.settingsItemGroupWrapper}>
        <div className={classes.settingsItem}>
          <SettingsSwitch
            name="register"
            label={<HelperWrapper t="sign-up-settings-label" verticalPosition={0} />}
            icon={<SignUpIcon />}
            handleChange={handleUpdateSignUp}
            checked={settings?.sign_up_enabled}
            disabled={isSettingsFetching || signUpIsLoading}
          />
        </div>
        <div className={classes.settingsItem}>
          <SettingsSwitch
            name="scan_to_earn_enabled"
            label={<HelperWrapper t="scan-to-earn-allow-settings-label" verticalPosition={0} />}
            icon={<AddCircleOutlineIcon />}
            handleChange={handleUpdateScanToEarn}
            checked={settings?.scan_to_earn_enabled}
            disabled={isSettingsFetching || isScanToEarnSettingLoading}
          />
        </div>
      </div>

      <Box
        className={classes.settingsItemGroupWrapper}
        sx={{
          backgroundColor: theme.palette.custom.lightGreen,
        }}
      >
        <GlobalSettingsVariables images={settings?.images_payload} />
      </Box>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>X Bot Post</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Cron Job: Post Tweet to X
          </Typography>
        </Box>

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Text Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_post || ''}
          field={ESettingsModelOrToolFields.X_BOT_POST}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_POST}
          tools={settings?.tools_payload?.x_bot_post}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-definition-label.v')}
          text={settings?.traicy_definition || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-post-label.v')}
          text={settings?.traicy_post || ''}
          field={ESettingsPromptFields.TRAICY_POST}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Image Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.traicy_image_with_text || ''}
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
          showImageSuportWarning
        />

        <TraicyPostImageSwitch
          field={ETraicyPostsPayloadFields.X_BOT_POST}
          value={settings?.traicy_posts_payload?.x_post_image || false}
          isLoading={isSettingsFetching || isUpdateTraicyPostsPayloadLoading}
          handleUpdate={handleUodateTraicyPostPayload}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT}
          tools={settings?.tools_payload?.traicy_image_with_text}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): Image with Text'}
          text={settings?.traicy_image_with_text_definition || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_DEFINITION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
        <Divider />

        <SettingsItem
          label={'Prompt (user): Image with Text'}
          text={settings?.traicy_image_with_text || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>X Bot Post 2</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Cron Job: Post Tweet to X 2
          </Typography>
        </Box>

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Text Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_post_2 || ''}
          field={ESettingsModelOrToolFields.X_BOT_POST_2}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_POST_2}
          tools={settings?.tools_payload?.x_bot_post_2}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-definition-label.v')}
          text={settings?.traicy_post_definition_2 || ''}
          field={ESettingsPromptFields.TRAICY_POST_DEFINITION_2}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-post-label.v')}
          text={settings?.traicy_post_2 || ''}
          field={ESettingsPromptFields.TRAICY_POST_2}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Image Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.traicy_image_with_text_2 || ''}
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_2}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
          showImageSuportWarning
        />

        <TraicyPostImageSwitch
          field={ETraicyPostsPayloadFields.X_BOT_POST_2}
          value={settings?.traicy_posts_payload?.x_post_image_2 || false}
          isLoading={isSettingsFetching || isUpdateTraicyPostsPayloadLoading}
          handleUpdate={handleUodateTraicyPostPayload}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_2}
          tools={settings?.tools_payload?.traicy_image_with_text_2}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): Image with Text'}
          text={settings?.traicy_image_with_text_definition_2 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_DEFINITION_2}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
        <Divider />

        <SettingsItem
          label={'Prompt (user): Image with Text'}
          text={settings?.traicy_image_with_text_2 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_2}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>X Bot Post 3</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Cron Job: Post Tweet to X 3
          </Typography>
        </Box>

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Text Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_post_3 || ''}
          field={ESettingsModelOrToolFields.X_BOT_POST_3}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_POST_3}
          tools={settings?.tools_payload?.x_bot_post_3}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-definition-label.v')}
          text={settings?.traicy_post_definition_3 || ''}
          field={ESettingsPromptFields.TRAICY_POST_DEFINITION_3}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-post-label.v')}
          text={settings?.traicy_post_3 || ''}
          field={ESettingsPromptFields.TRAICY_POST_3}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Image Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.traicy_image_with_text_3 || ''}
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_3}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
          showImageSuportWarning
        />

        <TraicyPostImageSwitch
          field={ETraicyPostsPayloadFields.X_BOT_POST_3}
          value={settings?.traicy_posts_payload?.x_post_image_3 || false}
          isLoading={isSettingsFetching || isUpdateTraicyPostsPayloadLoading}
          handleUpdate={handleUodateTraicyPostPayload}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_3}
          tools={settings?.tools_payload?.traicy_image_with_text_3}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): Image with Text'}
          text={settings?.traicy_image_with_text_definition_3 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_DEFINITION_3}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
        <Divider />

        <SettingsItem
          label={'Prompt (user): Image with Text'}
          text={settings?.traicy_image_with_text_3 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_3}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>X Bot Post 4</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Cron Job: Post Tweet to X 4
          </Typography>
        </Box>

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Text Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_post_4 || ''}
          field={ESettingsModelOrToolFields.X_BOT_POST_4}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_POST_4}
          tools={settings?.tools_payload?.x_bot_post_4}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-definition-label.v')}
          text={settings?.traicy_post_definition_4 || ''}
          field={ESettingsPromptFields.TRAICY_POST_DEFINITION_4}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-post-label.v')}
          text={settings?.traicy_post_4 || ''}
          field={ESettingsPromptFields.TRAICY_POST_4}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Image Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.traicy_image_with_text_4 || ''}
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_4}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
          showImageSuportWarning
        />

        <TraicyPostImageSwitch
          field={ETraicyPostsPayloadFields.X_BOT_POST_4}
          value={settings?.traicy_posts_payload?.x_post_image_4 || false}
          isLoading={isSettingsFetching || isUpdateTraicyPostsPayloadLoading}
          handleUpdate={handleUodateTraicyPostPayload}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_WITH_TEXT_4}
          tools={settings?.tools_payload?.traicy_image_with_text_4}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): Image with Text'}
          text={settings?.traicy_image_with_text_definition_4 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_DEFINITION_4}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
        <Divider />

        <SettingsItem
          label={'Prompt (user): Image with Text'}
          text={settings?.traicy_image_with_text_4 || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_WITH_TEXT_4}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>X Bot Hashtag Reply</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_hashtag_reply || ''}
          field={ESettingsModelOrToolFields.X_BOT_HASHTAG_REPLY}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_HASHTAG_REPLY}
          tools={settings?.tools_payload?.x_bot_hashtag_reply}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-system-hash-tag-reply-label.v')}
          text={settings?.traicy_definition_hash_tag_reply || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION_HASHTAG_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-hash-tag-reply-label.v')}
          text={settings?.traicy_hash_tag_reply || ''}
          field={ESettingsPromptFields.TRAICY_HASHTAG_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.REPLY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        />

        <Divider />

        <SettingsHashTags
          tags={settings?.traicy_hash_tag_list || []}
          label={t('settings-traicy-system-hash-tag-list-label.v')}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>X Bot Mentions Reply</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_mentions_reply || ''}
          field={ESettingsModelOrToolFields.X_BOT_MENTIONS_REPLY}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_MENTIONS_REPLY}
          tools={settings?.tools_payload?.x_bot_mentions_reply}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-system-mentions-reply-label.v')}
          text={settings?.traicy_definition_mentions_reply || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION_MENTIONS_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-mentions-reply-label.v')}
          text={settings?.traicy_mentions_reply || ''}
          field={ESettingsPromptFields.TRAICY_MENTIONS_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.COMMON_TRAICY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-system-message-check-label.v')}
          text={settings?.traicy_definition_message_check || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION_MESSAGE_CHECK}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description="This assistant is designed to classify tweets mentioning Traicy. It should determine if the message is valid and worthy of a reply by answering 'YES' (in all caps, without quotes)."
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-message-check-label.v')}
          text={settings?.traicy_message_check || ''}
          field={ESettingsPromptFields.TRAICY_MESSAGE_CHECK}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.MENTION_TRAICY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>X Bot Followed Users Tweets Reply</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.x_bot_followed_reply || ''}
          field={ESettingsModelOrToolFields.X_BOT_FOLLOWED_REPLY}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.X_BOT_FOLLOWED_REPLY}
          tools={settings?.tools_payload?.x_bot_followed_reply}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): X Bot Followed Users Reply'}
          text={settings?.traicy_definition_followed_reply || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION_FOLLOWED_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): X Bot Followed Users Reply'}
          text={settings?.traicy_followed_reply || ''}
          field={ESettingsPromptFields.TRAICY_FOLLOWED_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.COMMON_TRAICY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        />

        <Divider />

        <h5
          className={classes.itemGroupTitle}
          style={{ color: theme.palette.custom.grey2, fontSize: '14px' }}
        >
          Image Generation
        </h5>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.traicy_image_followed_reply || ''}
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_FOLLOWED_REPLY}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
          showImageSuportWarning
        />

        <TraicyPostImageSwitch
          field={ETraicyPostsPayloadFields.X_BOT_FOLLOWED_REPLY}
          value={settings?.traicy_posts_payload?.x_followed_reply_image || false}
          isLoading={isSettingsFetching || isUpdateTraicyPostsPayloadLoading}
          handleUpdate={handleUodateTraicyPostPayload}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.TRAICY_IMAGE_FOLLOWED_REPLY}
          tools={settings?.tools_payload?.traicy_image_followed_reply}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Traicy definition (system): Image'}
          text={settings?.traicy_image_definition_followed_reply || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_DEFINITION_FOLLOWED_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
        <Divider />

        <SettingsItem
          label={'Prompt (user): Image'}
          text={settings?.traicy_image_followed_reply || ''}
          field={ESettingsPromptFields.TRAICY_IMAGE_FOLLOWED_REPLY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TWEET_GENERATED_TEXT}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>Web Chat Bot</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Used by Traicy Web Chat, Traicy API, and Telegram Bot.
          </Typography>
        </Box>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.web_chat_bot || ''}
          field={ESettingsModelOrToolFields.WEB_CHAT_BOT}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.WEB_CHAT_BOT}
          tools={settings?.tools_payload?.web_chat_bot}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={t('settings-traicy-definition-chatbot-label.v')}
          text={settings?.traicy_definition_chatbot || ''}
          field={ESettingsPromptFields.TRAICY_DEFINITION_CHATBOT}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used:
  ${EPromptsVariables.CHATBOT_REPLY_VARIABLE}, 
  ${EPromptsVariables.TRAICY_CHATBOT_API_RESP_FORMAT} : 'markdown' | 'html' | 'plain_text'`}
        />

        <Divider />

        <SettingsItem
          label={t('settings-traicy-reply-chatbot-label.v')}
          text={settings?.traicy_reply_chatbot || ''}
          field={ESettingsPromptFields.TRAICY_REPLY_CHATBOT}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used:
  ${EPromptsVariables.CHATBOT_REPLY_VARIABLE}, 
  ${EPromptsVariables.TRAICY_CHATBOT_API_RESP_FORMAT} : 'markdown' | 'html' | 'plain_text'`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>Scan-to-Earn Validation</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Runs prior to the Details step, before gathering any consumer scan information
            (Product/Company name, Summary, Impacts).
          </Typography>
        </Box>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_validation || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_VALIDATION}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_VALIDATION}
          tools={settings?.tools_payload?.scan_to_earn_validation}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Media Validation'}
          text={settings?.scan_to_earn_definition_media_validation || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_MEDIA_VALIDATION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.SCAN_TO_EARN_BARCODE_VARIABLE} variable here, which will be replaced with the actual product Barcode ID .`}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Media Validation'}
          text={settings?.scan_to_earn_media_validation || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_MEDIA_VALIDATION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Scan-to-Earn Product Name</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_product_name || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_PRODUCT_NAME}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_PRODUCT_NAME}
          tools={settings?.tools_payload?.scan_to_earn_product_name}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Product Name'}
          text={settings?.scan_to_earn_definition_product_name || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_PRODUCT_NAME}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Product Name'}
          text={settings?.scan_to_earn_product_name || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_PRODUCT_NAME}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Scan-to-Earn Company Name</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_company_name || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_COMPANY_NAME}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_COMPANY_NAME}
          tools={settings?.tools_payload?.scan_to_earn_company_name}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Company Name'}
          text={settings?.scan_to_earn_definition_company_name || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_COMPANY_NAME}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Company Name'}
          text={settings?.scan_to_earn_company_name || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_COMPANY_NAME}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Scan-to-Earn Summary</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_summary || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_SUMMARY}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_SUMMARY}
          tools={settings?.tools_payload?.scan_to_earn_summary}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Summary'}
          text={settings?.scan_to_earn_definition_summary || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_SUMMARY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Summary'}
          text={settings?.scan_to_earn_summary || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_SUMMARY}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Scan-to-Earn Impacts</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_impacts || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_IMPACTS}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_IMPACTS}
          tools={settings?.tools_payload?.scan_to_earn_impacts}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Impacts'}
          text={settings?.scan_to_earn_definition_impacts || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_IMPACTS}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`Use the ${EPromptsVariables.SCAN_TO_EARN_IMPACTS_LIST_VARIABLE} variable here, which will be replaced with the full list of impacts from the 'impact_library' table.`}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Impacts'}
          text={settings?.scan_to_earn_impacts || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_IMPACTS}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h3 className={classes.itemGroupTitle}>Scan-to-Earn Info</h3>

          <Typography
            fontSize={'11px'}
            sx={{ color: theme.palette.custom.grey2, textAlign: 'center' }}
          >
            Runs before scan creation.
          </Typography>
        </Box>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_product_type || ''}
          showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_PRODUCT_TYPE}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_PRODUCT_TYPE}
          tools={settings?.tools_payload?.scan_to_earn_product_type}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Product Type'}
          text={settings?.scan_to_earn_definition_product_type || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_PRODUCT_TYPE}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LATITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LONGITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_BARCODE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_ACCOUNT_NAME_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_NAME_VARIABLE}`}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Product Type'}
          text={settings?.scan_to_earn_product_type || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_PRODUCT_TYPE}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LATITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LONGITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_BARCODE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_ACCOUNT_NAME_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_NAME_VARIABLE}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Scan-to-Earn Management</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.scan_to_earn_account_info || ''}
          // showImageSuportWarning
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_ACCOUNT_INFO}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.SCAN_TO_EARN_ACCOUNT_INFO}
          tools={settings?.tools_payload?.scan_to_earn_account_info}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Scan-to-earn definition (system): Account Info'}
          text={settings?.scan_to_earn_definition_account_info || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_ACCOUNT_INFO}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Account Info'}
          text={settings?.scan_to_earn_account_info || ''}
          field={ESettingsPromptFields.SCAN_TO_EARN_ACCOUNT_INFO}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.SCAN_TO_EARN_ACCOUNT_NAME_VARIABLE} (required), ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_NAME_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_TYPE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_DESCRIPTION_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LATITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LONGITUDE_VARIABLE} `}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Language Translation</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.language_translation || ''}
          field={ESettingsModelOrToolFields.LANGUAGE_TRANSLATION}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.LANGUAGE_TRANSLATION}
          tools={settings?.tools_payload?.language_translation}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Language Translation(system)'}
          text={settings?.language_translation_definition || ''}
          field={ESettingsPromptFields.LANGUAGE_TRANSLATION_DEFINITION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Language Translation'}
          text={settings?.language_translation || ''}
          field={ESettingsPromptFields.LANGUAGE_TRANSLATION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.LANGUAGE_TO_TRANSLATE}, ${EPromptsVariables.VALUE_TO_TRANSLATE}`}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Main Entities Descriptions</h3>

        <ModelSelect
          models={modelsList || []}
          activeModel={settings?.models_payload?.main_entities_descriptions || ''}
          field={ESettingsModelOrToolFields.MAIN_ENTITIES_DESCRIPRIONS}
          onModelChange={handleUpdateModelsPayload}
          isLoading={isSettingsFetching || isModelsPayloadLoading || idModelsListLoading}
        />

        <ToolsSection
          field={ESettingsModelOrToolFields.MAIN_ENTITIES_DESCRIPRIONS}
          tools={settings?.tools_payload?.main_entities_descriptions}
          handleUpdateTools={handleUpdateToolsPayload}
        />

        <SettingsItem
          label={'Descriptions(system)'}
          text={settings?.main_entities_descriptions_definition || ''}
          field={ESettingsPromptFields.MAIN_ENTITIES_DESCRIPRIONS_DEFINITION}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.ENTITY_DESCRIPRION_PROMPT}, ${EPromptsVariables.LANGUAGE_CODE}, ${EPromptsVariables.USER_PROMPT}`}
        />

        <Divider />

        <SettingsItem
          label={'Prompt (user): Descriptions'}
          text={settings?.main_entities_descriptions || ''}
          field={ESettingsPromptFields.MAIN_ENTITIES_DESCRIPRIONS}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.ENTITY_DESCRIPRION_PROMPT}, ${EPromptsVariables.LANGUAGE_CODE}, ${EPromptsVariables.USER_PROMPT}`}
        />

        <Divider />

        <DescriptionEntityPrompts
          descriptions={settings?.description_entity_payload}
          handleUpdate={handleUpdateDescriptionEntityPayload}
          isLoading={isDescriptionEntityPayloadLoading}
        />
      </div>

      <div className={classes.settingsItemGroupWrapper}>
        <h3 className={classes.itemGroupTitle}>Telegram Bot</h3>

        <TgSendMessagePermissions payload={settings?.tg_send_msg_permissions_payload} />

        <Divider />

        <SettingsTgChatsList
          field={ESettingsTgChatIdsFields.TG_MANUAL_MESSAG_CHATS}
          handleUpdate={handleUpdateTgChatList}
          label="Chat IDs for custom message"
          tags={settings?.tg_manual_message_chats || []}
        />

        <SettingsTgSendCustomMessage
          placeholder="Enter a one-off message to broadcast to all chats specified in list"
          isLoading={isSettingsLoading || isSendTgCustomMessageLoading}
          onSend={handleSendTgCustomMessage}
        />

        <Divider />

        <SettingsTgChatsList
          field={ESettingsTgChatIdsFields.TG_APPROVED_SCANS_DAILY_SUMMARY_CHATS}
          handleUpdate={handleUpdateTgChatList}
          label="Chat IDs for daily summary"
          tags={settings?.tg_approved_scans_daily_summary_chats || []}
        />

        <SettingsItem
          label={'Approved scans daily summary'}
          text={settings?.tg_bot_approved_scans_daily_summary_message || ''}
          field={ESettingsPromptFields.TG_BOT_APPROVED_SCANS_DAILY_SUMMARY_MESSAGE}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TG_BOT_APPROVED_SCANS_COUNT}`}
          showGlobalVariables={false}
        />

        <Divider />

        <SettingsTgChatsList
          field={ESettingsTgChatIdsFields.TG_SCAN_APPROVED_CHATS}
          handleUpdate={handleUpdateTgChatList}
          label="Chat IDs for scan creation"
          tags={settings?.tg_scan_approved_chats || []}
        />

        <SettingsItem
          label={'Scan creation notification'}
          text={settings?.tg_bot_approved_scan_message || ''}
          field={ESettingsPromptFields.TG_BOT_APPROVED_SCAN_MESSAGE}
          onSave={handleUpdateTraicyPrompt}
          isLoading={isTraicyPromptLoading || isSettingsLoading}
          description={`The following variables can be used: ${EPromptsVariables.TG_BOT_ACCOUNT_NAME}, ${EPromptsVariables.TG_BOT_PRODUCT_NAME}, ${EPromptsVariables.TG_BOT_BARCODE}, ${EPromptsVariables.TG_BOT_USER_NAME}, ${EPromptsVariables.TG_BOT_PRODUCT_TYPE}, ${EPromptsVariables.TG_BOT_USER_TX_LINK}, ${EPromptsVariables.TG_BOT_COUNTRY_FLAG}`}
          showGlobalVariables={false}
        />
      </div>
    </Box>
  )
}
